import { useState, useEffect } from 'react';
import AnalyticsModal from './AnalyticsModal';

// Parse French locale numbers (virgule as decimal separator)
const parseLocaleNumber = (value) => {
    if (typeof value === 'number') return value;
    return Number(String(value).replace(',', '.'));
};

// Format number to French locale (virgule as decimal separator)
const formatLocaleNumber = (value, decimals = 2) => {
    return Number(value || 0).toFixed(decimals).replace('.', ',');
};

const getLastPriceValue = (item, movements = []) => {
    const directPrice = Number(
        item?.last_price ??
        item?.lastPrice ??
        item?.purchasePrice ??
        item?.cost_per_unit ??
        item?.purchase_price ??
        item?.initial_price ??
        item?.stock_initial_price ??
        item?.starting_price ??
        item?.price ??
        item?.unit_cost ??
        0
    );
    if (directPrice > 0) {
        return directPrice;
    }

    const purchaseMovement = (movements || [])
        .filter((movement) => {
            const movementTargetId = movement?.stockable_id ?? movement?.stockable?.id ?? movement?.ingredient_id ?? movement?.ingredient?.id ?? movement?.item_id ?? movement?.item?.id;
            if (String(movementTargetId) !== String(item?.id)) {
                return false;
            }

            const type = String(movement?.type || '').toLowerCase();
            const quantity = Number(movement?.quantity ?? movement?.qty ?? 0);
            const hasPrice = getUnitPriceFromMovement(movement, null) !== null;
            return type === 'purchase' || type === 'adjustment' || quantity > 0 || hasPrice;
        })
        .sort((a, b) => {
            const left = new Date(a?.movement_date || a?.created_at || a?.purchase_date || 0).getTime();
            const right = new Date(b?.movement_date || b?.created_at || b?.purchase_date || 0).getTime();
            return right - left;
        })[0];

    const movementPrice = getUnitPriceFromMovement(purchaseMovement, directPrice);

    return movementPrice > 0 ? movementPrice : directPrice;
};

const getLatestPriceForItem = async (item, movements = []) => {
    const fallbackPrice = getLastPriceValue(item, movements);
    const fallbackUnit = (item?.unit?.name ?? item?.unit ?? item?.unit_name) || '';
    const fallbackQuantity = Number(item?.stock_quantity ?? item?.stock ?? item?.quantity ?? 0) || 1;

    if (!item?.id) {
        return { price: fallbackPrice, quantity: fallbackQuantity, unit: fallbackUnit };
    }

    return {
        price: fallbackPrice,
        quantity: fallbackQuantity,
        unit: fallbackUnit,
    };
};

const getUnitPriceFromMovement = (movement, fallbackPrice = null) => {
    if (!movement) return null;

    const batch = movement?.batch || {};
    
    // batch.unit_cost is actually TOTAL purchase price (prix d'achat total)
    // We need to divide by received_quantity to get unit price
    const batchTotalPrice = Number(batch?.unit_cost ?? null);
    const batchReceivedQty = Number(batch?.received_quantity ?? null);
    if (batchTotalPrice > 0 && batchReceivedQty > 0) {
        return batchTotalPrice / batchReceivedQty;
    }

    // If movement has unit_cost (total price) and quantity, compute unit price
    const mvTotalPrice = Number(movement?.unit_cost ?? null);
    const mvQty = Number(movement?.quantity ?? null);
    if (mvTotalPrice > 0 && mvQty > 0) {
        return mvTotalPrice / mvQty;
    }

    // Final fallback
    return fallbackPrice && Number(fallbackPrice) > 0 ? Number(fallbackPrice) : null;
};

const exportPricesCsv = async (items = [], movements = []) => {
    const rows = [['nom', 'quantité', 'unité', 'dernier prix']];
    const details = await Promise.all(items.map((item) => getLatestPriceForItem(item, movements)));

    items.forEach((item, index) => {
        const name = String(item.name ?? '').replace(/"/g, '""');
        const price = formatLocaleNumber(details[index]?.price ?? 0, 2);
        const quantity = formatLocaleNumber(details[index]?.quantity ?? 1, 0);
        const unit = String(details[index]?.unit ?? '').replace(/"/g, '""');
        rows.push([`"${name}"`, quantity, `"${unit}"`, price]);
    });

    const csvContent = '\uFEFF' + rows.map((row) => row.join(';')).join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `prix-ingredients-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const exportStockDetails = async (items = [], movements = [], computeCalculatedStock = () => 0, getStockValue = () => 0) => {
    const CUTOFF_DATE = new Date('2026-07-01');

    // Calculer les statistiques pour chaque ingrédient
    const getStockStats = (item) => {
        if (!item || !item.id) return null;

        const itemMovements = (movements || []).filter((m) => {
            const movementTargetId = m?.stockable_id ?? m?.stockable?.id ?? m?.ingredient_id ?? m?.ingredient?.id ?? m?.item_id ?? m?.item?.id;
            return String(movementTargetId) === String(item.id);
        });

        // Filtrer les achats (purchase) à partir du 01 juillet 2026
        const purchasesSince = itemMovements.filter((m) => {
            const type = String(m.type || '').toLowerCase();
            if (type !== 'purchase') return false;
            const moveDate = new Date(m.batch?.purchase_date || m.movement_date || m.created_at || 0);
            return moveDate >= CUTOFF_DATE;
        });

        // Dernière date d'achat et prix unitaire du dernier achat
        let lastPurchaseDate = '';
        let lastUnitPrice = 0;
        if (purchasesSince.length > 0) {
            const sortedPurchases = [...purchasesSince].sort((a, b) => {
                const left = new Date(a?.batch?.purchase_date || a?.movement_date || 0).getTime();
                const right = new Date(b?.batch?.purchase_date || b?.movement_date || 0).getTime();
                return right - left;
            });
            const lastPurchase = sortedPurchases[0];
            const lastDate = lastPurchase?.batch?.purchase_date || lastPurchase?.movement_date;
            if (lastDate) {
                lastPurchaseDate = new Date(lastDate).toLocaleDateString('fr-FR');
            }
            lastUnitPrice = getUnitPriceFromMovement(lastPurchase, 0) || 0;
        }

        // Calculer la quantité totale achetée depuis le 01 juillet
        const totalQuantityPurchased = purchasesSince.reduce((sum, m) => {
            return sum + Number(m.quantity ?? m.qty ?? 0);
        }, 0);

        // Calculer le prix unitaire moyen depuis le 01 juillet
        let totalCostPurchased = 0;
        purchasesSince.forEach((m) => {
            const unitPrice = getUnitPriceFromMovement(m, 0) || 0;
            const quantity = Number(m.quantity ?? m.qty ?? 0);
            totalCostPurchased += unitPrice * quantity;
        });
        const avgUnitPrice = totalQuantityPurchased > 0 ? totalCostPurchased / totalQuantityPurchased : 0;

        // Calculer le nombre de jours écoulés depuis le 01 juillet 2026
        const today = new Date();
        const daysElapsed = Math.max(1, Math.floor((today - CUTOFF_DATE) / (1000 * 60 * 60 * 24)));
        const monthsElapsed = Math.max(1, daysElapsed / 30);

        // Moyennes d'achat
        const avgPerDay = daysElapsed > 0 ? totalQuantityPurchased / daysElapsed : 0;
        const avgPerMonth = monthsElapsed > 0 ? totalQuantityPurchased / monthsElapsed : 0;

        return {
            totalQuantityPurchased,
            avgPerDay,
            avgPerMonth,
            lastPurchaseDate,
            lastUnitPrice,
            avgUnitPrice,
        };
    };

    // Préparer les données pour l'export
    const exportData = items.map((item) => {
        const stats = getStockStats(item);
        const currentStock = computeCalculatedStock(item);
        const stockValue = getStockValue(item);

        return {
            nom: String(item.name ?? ''),
            marque: String(item.marque ?? ''),
            caracteristique: String(item.caracteristique ?? ''),
            type: item.item_type === 'consumable' ? 'Consommable' : 'Ingrédient',
            stock_actuel: Number(currentStock || 0),
            unite: String(item.unit?.name || item.unit || ''),
            quantite_achetee: stats?.totalQuantityPurchased ?? 0,
            moyenne_par_jour: stats?.avgPerDay ?? 0,
            moyenne_par_mois: stats?.avgPerMonth ?? 0,
            derniere_achat: stats?.lastPurchaseDate || '',
            dernier_prix_unitaire: stats?.lastUnitPrice ?? 0,
            prix_unitaire_moyen: stats?.avgUnitPrice ?? 0,
            valeur_stock: Number(stockValue || 0),
        };
    });

    // Calculer les totaux
    const totalStockValue = exportData.reduce((sum, row) => sum + row.valeur_stock, 0);

    // Créer le CSV
    const headers = [
        'Nom',
        'Marque',
        'Caractéristique',
        'Type',
        'Stock actuel',
        'Unité',
        'Quantité achetée depuis 01/07',
        'Moyenne par jour',
        'Moyenne par mois',
        'Dernière date d\'achat',
        'Dernier prix unitaire (Ar)',
        'Prix unitaire moyen (Ar)',
        'Valeur du stock (Ar)',
    ];

    const rows = [headers];

    // Ajouter les lignes de données
    exportData.forEach((row) => {
        rows.push([
            row.nom,
            row.marque,
            row.caracteristique,
            row.type,
            row.stock_actuel,
            row.unite,
            row.quantite_achetee,
            formatLocaleNumber(row.moyenne_par_jour, 2),
            formatLocaleNumber(row.moyenne_par_mois, 2),
            row.derniere_achat,
            formatLocaleNumber(row.dernier_prix_unitaire, 2),
            formatLocaleNumber(row.prix_unitaire_moyen, 2),
            formatLocaleNumber(row.valeur_stock, 2),
        ]);
    });

    // Ajouter une ligne de total
    rows.push([
        'TOTAL',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        formatLocaleNumber(totalStockValue, 2),
    ]);

    // Créer le contenu CSV avec séparateur virgule pour Excel
    const csvContent = rows
        .map((row) =>
            row
                .map((cell) => {
                    const cellStr = String(cell ?? '');
                    // Échapper les guillemets et mettre entre guillemets si contient virgule ou guillemets
                    if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                })
                .join(',')
        )
        .join('\r\n');

    // Ajouter BOM UTF-8 pour Excel
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `export-stock-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const getMovementOriginLabel = (movement) => {
    if (!movement) return '—';
    const type = String(movement.type || '').toLowerCase();
    if (type === 'inventory') return 'Inventaire';
    if (type === 'purchase') return 'Réception';
    if (type === 'adjustment') return 'Ajustement';
    if (type === 'consumption') return 'Sortie';
    if (type === 'waste') return 'Perte';
    if (type === 'return') return 'Retour';
    if (type === 'sale') return 'Vente';
    if (type === 'transfer') return 'Transfert';
    const reference = String(movement.reference || movement.notes || movement.note || '').toLowerCase();
    if (reference.includes('inventory')) return 'Inventaire';
    return 'Autre';
};

const getSupplierName = (supplier, supplierMap = new Map()) => {
    if (!supplier) return '—';
    if (typeof supplier === 'string') {
        const trimmed = supplier.trim();
        return trimmed || '—';
    }

    if (typeof supplier === 'object') {
        return supplier.companyName || supplier.company_name || supplier.name || supplier.supplier_name || supplier.label || '—';
    }

    return '—';
};

const getSupplyOrderSupplierEntries = (item, supplyOrders = [], suppliers = []) => {
    if (!item) return [];

    const supplierMap = new Map((suppliers || []).map((supplier) => [String(supplier.id), supplier]));
    const bySupplier = new Map();
    const itemName = String(item?.name || item?.item_name || '').trim().toLowerCase();
    const normalizedItemName = itemName.normalize('NFD').replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');

    const parsePrice = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        const clean = String(value).replace(/\s/g, '').replace(/,/g, '.');
        const parsed = Number(clean);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    (supplyOrders || []).forEach((order) => {
        const targetId = String(order?.target_id ?? order?.targetId ?? order?.ingredient_id ?? order?.ingredientId ?? order?.item_id ?? order?.itemId ?? '');
        const itemNameFromOrder = String(order?.item_name || order?.itemName || order?.name || '').trim().toLowerCase();
        const normalizedOrderItemName = itemNameFromOrder.normalize('NFD').replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');
        const targetMatches = targetId && item?.id ? String(targetId) === String(item.id) : false;
        const nameMatches = normalizedItemName && normalizedOrderItemName ? normalizedOrderItemName.includes(normalizedItemName) || normalizedItemName.includes(normalizedOrderItemName) : false;
        if (!targetMatches && !nameMatches) return;

        const totalPrice = parsePrice(order?.purchase_price ?? order?.purchasePrice ?? null);
        const quantity = parsePrice(order?.received_quantity ?? order?.receivedQuantity ?? order?.quantity ?? order?.qty ?? 0);
        const price = totalPrice > 0 && quantity > 0 ? totalPrice / quantity : parsePrice(order?.price ?? 0);
        const supplierValue = order?.supplier ?? order?.supplier_name ?? order?.companyName ?? order?.company_name ?? order?.provider ?? null;
        const supplierId = order?.supplier_id ?? order?.supplierId ?? order?.supplierID ?? null;
        if (!supplierValue && !supplierId) return;
        let supplierName = '';

        if (supplierValue) {
            supplierName = getSupplierName(supplierValue, supplierMap);
        } else if (supplierId) {
            const found = supplierMap.get(String(supplierId));
            supplierName = found ? getSupplierName(found, supplierMap) : '—';
        }

        if (!supplierName || supplierName === '—') {
            supplierName = 'Fournisseur non renseigné';
        }

        const key = supplierName;
        const date = order?.purchase_date || order?.purchaseDate || order?.order_date || order?.orderDate || order?.created_at || null;
        const existing = bySupplier.get(key);
        const candidate = { supplier: supplierName, price, date };

        if (!existing) {
            bySupplier.set(key, candidate);
            return;
        }

        const currentDate = new Date(existing.date || 0).getTime();
        const nextDate = new Date(date || 0).getTime();
        if (nextDate > currentDate) {
            bySupplier.set(key, candidate);
        }
    });

    return Array.from(bySupplier.values()).sort((left, right) => {
        const leftPrice = Number(left.price || 0);
        const rightPrice = Number(right.price || 0);
        if (leftPrice === rightPrice) return 0;
        return leftPrice - rightPrice;
    });
};

function SupplierPricesModal({ item, isOpen, onClose, entries = [] }) {
    const [quantity, setQuantity] = useState('1');

    useEffect(() => {
        if (isOpen) {
            setQuantity('1');
        }
    }, [isOpen]);

    if (!isOpen || !item) return null;

    const parsedQuantity = Number(String(quantity || '0').replace(',', '.'));
    const quantityValue = Number.isFinite(parsedQuantity) && parsedQuantity >= 0 ? parsedQuantity : 0;

    return (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/50 p-4" onClick={onClose}>
            <div className="mx-auto flex min-h-full items-center justify-center">
                <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
                    <div className="border-b border-green-200 p-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-xl font-semibold text-green-900">Fournisseurs et prix unitaires</h3>
                                <p className="mt-1 text-sm text-green-600">{item.name}</p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-full border border-green-200 px-3 py-1 text-2xl text-green-600 transition hover:bg-green-50 hover:text-green-900"
                            >
                                ×
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="mb-5 grid gap-3 md:grid-cols-2">
                            <label className="space-y-2 text-sm font-medium text-green-900">
                                Quantité simulée
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={quantity}
                                    onChange={(event) => setQuantity(event.target.value)}
                                    className="w-full rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-slate-900"
                                />
                            </label>
                            <div className="rounded-3xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                                <p className="font-semibold text-green-900">Simulation de prix</p>
                            </div>
                        </div>
                        {entries.length === 0 ? (
                            <p className="text-sm text-green-600">Aucune commande d'approvisionnement avec prix unitaire n'a été trouvée pour cet article.</p>
                        ) : (
                            <div className="space-y-3">
                                {entries.map((entry) => (
                                    <div key={`${entry.supplier}-${entry.price}`} className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <p className="font-semibold text-green-900">{entry.supplier}</p>
                                                {entry.date ? <p className="text-xs text-green-600">{new Date(entry.date).toLocaleDateString('fr-FR')}</p> : null}
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-semibold text-green-700">{formatLocaleNumber(entry.price, 2)} Ar / {item.unit || 'unité'}</p>
                                                {quantityValue > 0 ? (
                                                    <p className="mt-1 text-xs text-green-600">Total estimé : {formatLocaleNumber(entry.price * quantityValue, 2)} Ar</p>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function IngredientDetailsModal({ ingredient, isOpen, onClose }) {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen || !ingredient) {
            setDetails(null);
            return;
        }

        if (ingredient.item_type === 'consumable') {
            setDetails({
                ...ingredient,
                movements: [],
                stock_initial: ingredient.stock_initial ?? ingredient.stock ?? 0,
            });
            setLoading(false);
            return;
        }

        const fetchDetails = async () => {
            setLoading(true);
            try {
                const response = await fetch(`/api/inventory/ingredients/${ingredient.id}`, {
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (response.ok) {
                    const data = await response.json();
                    setDetails({
                        ...data,
                        stock_initial: data.stock_initial ?? data.stock ?? ingredient.stock ?? 0,
                    });
                } else {
                    setDetails({
                        ...ingredient,
                        movements: [],
                        stock_initial: ingredient.stock_initial ?? ingredient.stock ?? 0,
                    });
                }
            } catch (error) {
                console.error('Erreur chargement détails:', error);
                setDetails({
                    ...ingredient,
                    movements: [],
                    stock_initial: ingredient.stock_initial ?? ingredient.stock ?? 0,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [ingredient, isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !ingredient) return null;

    if (loading && !details) {
        return (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4" onClick={onClose}>
                <div className="mx-auto flex min-h-full items-center justify-center">
                    <div className="rounded-3xl bg-white p-8 shadow-2xl" onClick={(event) => event.stopPropagation()}>
                        <p>Chargement...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!details) return null;

    const isConsumable = details.item_type === 'consumable' || ingredient?.item_type === 'consumable';
    const purchaseMovements = (details.movements || []).filter(
        (m) => m.type === 'purchase' || Number(m.quantity) > 0
    );
    const saleMovements = (details.movements || []).filter(
        (m) => m.type !== 'purchase' && Number(m.quantity) < 0
    );

    const initialStockEntry = details.stock_initial !== undefined ? [{
        id: 'initial',
        quantity: details.stock_initial,
        reference: 'Stock initial',
        purchaseDate: details.stock_initial_date || details.created_at || null,
        deliveryDate: null,
        purchasePrice: (() => {
            const initialPriceValue = Number(
                details?.initial_price ??
                details?.stock_initial_price ??
                details?.starting_price ??
                details?.last_price ??
                details?.cost_per_unit ??
                details?.purchasePrice ??
                details?.purchase_price ??
                0
            );
            return initialPriceValue > 0 ? initialPriceValue : null;
        })(),
        supplier: details.supplier || null,
        notes: 'Stock initial estimé',
        orderReference: 'Stock initial',
    }] : [];

    const purchaseEntries = [
        ...initialStockEntry,
        ...purchaseMovements.map((movement) => ({
            id: movement.id,
            quantity: movement.batch?.received_quantity ?? movement.quantity,
            orderReference: movement.batch?.lot_number || movement.reference || 'BC',
            purchaseDate: movement.batch?.purchase_date || movement.movement_date || null,
            deliveryDate: movement.batch?.delivery_date || movement.actual_delivery_date || movement.received_date || null,
            purchasePrice: getUnitPriceFromMovement(
                movement,
                details?.last_price ?? details?.cost_per_unit ?? details?.purchasePrice ?? null
            ),
            supplier: movement.batch?.supplier || movement.supplier || movement.user?.name || null,
            notes: movement.notes || movement.batch?.notes || '',
            origin: getMovementOriginLabel(movement),
        }))
    ];

    // Calculer le stock courant en prenant comme base le dernier inventaire (si présent)
    const calculatedStock = (() => {
        const movementsList = details.movements || [];
        // repérer le dernier mouvement d'inventaire
        const inventoryMovs = (movementsList || []).filter((m) => {
            const type = String(m.type || '').toLowerCase();
            const reference = String(m.reference || m.notes || '').toLowerCase();
            return type === 'inventory' || type === 'adjustment' || reference.includes('inventory');
        });

        let baseQty = Number(details.stock_initial ?? details.stock_quantity ?? details.stock ?? 0);
        let inventoryDate = null;
        if (inventoryMovs.length > 0) {
            inventoryMovs.sort((a, b) => {
                const left = new Date(a?.movement_date || a?.created_at || 0).getTime();
                const right = new Date(b?.movement_date || b?.created_at || 0).getTime();
                return right - left;
            });
            const lastInv = inventoryMovs[0];
            baseQty = Number(lastInv.quantity_after ?? lastInv.stock_after ?? lastInv.quantity ?? lastInv.qty ?? lastInv.stock ?? baseQty);
            inventoryDate = new Date(lastInv.movement_date || lastInv.created_at || 0);
        }

        // Ajouter tous les mouvements intervenus après la date de l'inventaire
        const delta = (movementsList || []).reduce((acc, m) => {
            const mDate = new Date(m.movement_date || m.created_at || 0);
            if (inventoryDate && mDate.getTime() <= inventoryDate.getTime()) return acc;
            const q = Number(m.quantity ?? m.qty ?? 0);
            return acc + q;
        }, 0);

        return baseQty + delta;
    })();

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4" onClick={onClose}>
            <div className="mx-auto flex min-h-full items-center justify-center">
                <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
                    <div className="flex max-h-[90vh] flex-col">
                        <div className="flex-shrink-0 border-b border-green-200 p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl font-semibold text-green-900">{details.name}{(details.marque || details.caracteristique) ? ` (${[details.marque, details.caracteristique].filter(Boolean).join(' ')})` : ''}</h2>
                                    <p className="mt-1 text-sm text-green-600">
                                        Stock actuel: <span className="font-semibold">{formatLocaleNumber(calculatedStock ?? details.stock_quantity ?? details.stock ?? 0, 0)} {details.unit?.name || '—'}</span>
                                    </p>
                                    {details.stock_initial !== undefined && (
                                        <p className="text-sm text-green-600">
                                            Stock initial estimé: <span className="font-semibold">{formatLocaleNumber(details.stock_initial, 0)} {details.unit?.name || '—'}</span>
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-full border border-green-200 px-3 py-1 text-2xl text-green-600 transition hover:bg-green-50 hover:text-green-900"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="space-y-6">
                                {isConsumable ? (
                                    <div className="rounded-3xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                                        Ce consommable est suivi au niveau du stock courant. Son historique détaillé sera ajouté prochainement si nécessaire.
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <h3 className="mb-3 text-lg font-semibold text-green-900">Historique des achats</h3>
                                            <div className="overflow-x-auto rounded-3xl border border-green-200 bg-green-50">
                                                <table className="min-w-full divide-y divide-green-200 text-left text-sm text-slate-900">
                                                    <thead className="bg-green-100 text-green-900">
                                                        <tr>
                                                            <th className="px-4 py-3 font-medium">BC / source</th>
                                                            <th className="px-4 py-3 font-medium">Date achat</th>
                                                            <th className="px-4 py-3 font-medium">Date livraison</th>
                                                            <th className="px-4 py-3 font-medium">Prix unitaire</th>
                                                            <th className="px-4 py-3 font-medium">Quantité</th>
                                                            <th className="px-4 py-3 font-medium">Fournisseur</th>
                                                            <th className="px-4 py-3 font-medium">Origine</th>
                                                            <th className="px-4 py-3 font-medium">Note</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-green-100 bg-slate-50">
                                                        {purchaseEntries.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="8" className="px-4 py-6 text-center text-sm text-green-600">
                                                                    Stock initial : aucune entrée d'achat enregistrée.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            purchaseEntries.map((entry) => (
                                                                <tr key={entry.id} className="hover:bg-slate-100 even:bg-white">
                                                                    <td className="px-4 py-3 text-slate-900">{entry.orderReference}</td>
                                                                    <td className="px-4 py-3 text-slate-900">{formatDateTime(entry.purchaseDate)}</td>
                                                                    <td className="px-4 py-3 text-slate-900">{formatDateTime(entry.deliveryDate)}</td>
                                                                    <td className="px-4 py-3 text-right text-slate-900">
                                                                        {entry.purchasePrice !== undefined && entry.purchasePrice !== null
                                                                            ? `${formatLocaleNumber(entry.purchasePrice, 2)} Ar`
                                                                            : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-slate-900">+{formatLocaleNumber(Math.abs(Number(entry.quantity)), 0)}</td>
                                                                    <td className="px-4 py-3 text-slate-900">{entry.supplier || '—'}</td>
                                                                    <td className="px-4 py-3 text-slate-900">{entry.origin || '—'}</td>
                                                                    <td className="px-4 py-3 text-slate-900">{entry.notes || '—'}</td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="mb-3 text-lg font-semibold text-green-900">Historique des ventes</h3>
                                            <div className="overflow-x-auto rounded-3xl border border-green-200 bg-green-50">
                                                <table className="min-w-full divide-y divide-green-200 text-left text-sm text-slate-700">
                                                    <thead className="bg-green-100 text-green-900">
                                                        <tr>
                                                            <th className="px-4 py-3 font-medium">Date</th>
                                                            <th className="px-4 py-3 font-medium">Quantité</th>
                                                            <th className="px-4 py-3 font-medium">Référence</th>
                                                            <th className="px-4 py-3 font-medium">Origine</th>
                                                            <th className="px-4 py-3 font-medium">Note</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-green-100 bg-white">
                                                        {saleMovements.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="5" className="px-4 py-6 text-center text-sm text-green-600">
                                                                    Aucune sortie enregistrée pour cet ingrédient.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            saleMovements.map((movement) => (
                                                                <tr key={movement.id} className="hover:bg-green-50">
                                                                    <td className="px-4 py-3">
                                                                        {movement.movement_date
                                                                            ? new Date(movement.movement_date).toLocaleDateString('fr-FR')
                                                                            : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {formatLocaleNumber(Math.abs(Number(movement.quantity)), 0)}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {movement.reference || movement.batch?.lot_number || '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {getMovementOriginLabel(movement)}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {movement.notes || movement.batch?.supplier || '—'}
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex-shrink-0 border-t border-green-200 p-6">
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-3xl bg-green-600 px-6 py-2 font-semibold text-white hover:bg-green-700"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}



export default function StockTab({ menuItems = [], ingredients = [], movements = [], alerts = [], getPortionsRemaining, supplyOrders = [], suppliers = [] }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIngredientId, setSelectedIngredientId] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
    const [supplierModalOpen, setSupplierModalOpen] = useState(false);
    const [supplierModalItem, setSupplierModalItem] = useState(null);
    const [supplierModalEntries, setSupplierModalEntries] = useState([]);
    const [activeItemType, setActiveItemType] = useState('all');
    
    // Filtres par colonne
    const [filterName, setFilterName] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStockMin, setFilterStockMin] = useState('');
    const [filterStockMax, setFilterStockMax] = useState('');
    const [filterThresholdMin, setFilterThresholdMin] = useState('');
    const [filterThresholdMax, setFilterThresholdMax] = useState('');
    const [filterUnit, setFilterUnit] = useState('');
    const [filterValueMin, setFilterValueMin] = useState('');
    const [filterValueMax, setFilterValueMax] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    
    // États pour le tri
    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');

    const selectedIngredient = (ingredients || []).find((ingredient) => ingredient.id === selectedIngredientId) || null;

    const dishStatus = (menuItems || []).map((item) => ({
        ...item,
        portions: getPortionsRemaining(item),
    }));

    // Compute calculated stock for an ingredient based on last inventory snapshot + subsequent movements
    const computeCalculatedStock = (item) => {
        if (!item || !item.id) return Number(item?.stock ?? item?.stock_quantity ?? 0);
        const itemMovements = (movements || []).filter((m) => {
            const movementTargetId = m?.stockable_id ?? m?.stockable?.id ?? m?.ingredient_id ?? m?.ingredient?.id ?? m?.item_id ?? m?.item?.id;
            return String(movementTargetId) === String(item.id);
        });

        // find inventory/adjustment movements for this item
        const inventoryMovs = (itemMovements || []).filter((m) => {
            const type = String(m.type || '').toLowerCase();
            const reference = String(m.reference || m.notes || '').toLowerCase();
            if (type === 'inventory') {
                return true;
            }
            if (type === 'adjustment') {
                return reference.includes('inventory');
            }
            return false;
        });

        let baseQty = Number(item.stock_initial ?? item.stock_quantity ?? item.stock ?? 0);
        let inventoryDate = null;
        if (inventoryMovs.length > 0) {
            inventoryMovs.sort((a, b) => {
                const left = new Date(a?.movement_date || a?.created_at || 0).getTime();
                const right = new Date(b?.movement_date || b?.created_at || 0).getTime();
                return right - left;
            });
            const lastInv = inventoryMovs[0];
            baseQty = Number(lastInv.quantity_after ?? lastInv.stock_after ?? lastInv.quantity ?? lastInv.qty ?? lastInv.stock ?? baseQty);
            inventoryDate = new Date(lastInv.movement_date || lastInv.created_at || 0);
        }

        const delta = (itemMovements || []).reduce((acc, m) => {
            const mDate = new Date(m.movement_date || m.created_at || 0);
            if (inventoryDate && mDate.getTime() <= inventoryDate.getTime()) return acc;
            const q = Number(m.quantity ?? m.qty ?? 0);
            return acc + q;
        }, 0);

        return baseQty + delta;
    };

    const getStockValue = (item) => {
        const price = getLastPriceValue(item, movements);
        const quantity = Math.max(0, computeCalculatedStock(item));
        return price * quantity;
    };

    const getStockStatus = (ingredient) => {
        const calculatedStock = computeCalculatedStock(ingredient);
        const threshold = Number(ingredient?.threshold ?? 0);

        if (calculatedStock <= 0) return 'En rupture';
        if (calculatedStock <= threshold) return 'En rupture';
        if (calculatedStock <= threshold * 1.2) return 'Proche du seuil';
        return 'OK';
    };

    const filteredIngredients = (ingredients || []).filter((ingredient) => {
        // Filtres existants
        const matchesSearch = !searchTerm.trim() || ingredient.name.toLowerCase().includes(searchTerm.trim().toLowerCase());
        const matchesItemType = activeItemType === 'all' || (ingredient.item_type || 'ingredient') === activeItemType;

        // Nouveaux filtres par colonne
        const matchesName = !filterName.trim() || ingredient.name.toLowerCase().includes(filterName.trim().toLowerCase());
        const matchesType = filterType === 'all' || (ingredient.item_type || 'ingredient') === filterType;
        
        const calculatedStock = computeCalculatedStock(ingredient);
        const matchesStock = (!filterStockMin || calculatedStock >= parseFloat(filterStockMin)) && 
                            (!filterStockMax || calculatedStock <= parseFloat(filterStockMax));
        
        const matchesThreshold = (!filterThresholdMin || ingredient.threshold >= parseFloat(filterThresholdMin)) && 
                                (!filterThresholdMax || ingredient.threshold <= parseFloat(filterThresholdMax));
        
        const matchesUnit = !filterUnit.trim() || (ingredient.unit || '').toLowerCase().includes(filterUnit.trim().toLowerCase());
        
        const stockValue = getStockValue(ingredient);
        const matchesValue = (!filterValueMin || stockValue >= parseFloat(filterValueMin)) && 
                            (!filterValueMax || stockValue <= parseFloat(filterValueMax));

        return matchesSearch && matchesItemType && matchesName && matchesType && matchesStock && 
               matchesThreshold && matchesUnit && matchesValue;
    });

    const totalStockValue = (ingredients || []).reduce((sum, ingredient) => sum + getStockValue(ingredient), 0);

    const missingIngredients = (filteredIngredients || [])
        .map((ingredient) => ({
            ...ingredient,
            _calculatedStock: computeCalculatedStock(ingredient),
        }))
        .filter((ingredient) => (ingredient._calculatedStock ?? 0) <= (ingredient.threshold ?? 0))
        .map((ingredient) => ({
            ...ingredient,
            missing: Math.max(0, (ingredient.threshold ?? 0) - (ingredient._calculatedStock ?? 0) + 1),
        }));

    const openDetailsModal = (ingredient) => {
        setSelectedIngredientId(ingredient.id);
        setDetailsModalOpen(true);
    };

    const openSupplierModal = (ingredient) => {
        setSupplierModalItem(ingredient);
        setSupplierModalEntries(getSupplyOrderSupplierEntries(ingredient, supplyOrders, suppliers));
        setSupplierModalOpen(true);
    };

    // Fonction pour réinitialiser tous les filtres
    const resetFilters = () => {
        setFilterName('');
        setFilterType('all');
        setFilterStockMin('');
        setFilterStockMax('');
        setFilterThresholdMin('');
        setFilterThresholdMax('');
        setFilterUnit('');
        setFilterValueMin('');
        setFilterValueMax('');
    };

    // Fonction pour gérer le tri
    const handleSort = (columnName) => {
        if (sortColumn === columnName) {
            // Si on clique sur la même colonne, inverser l'ordre
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Sinon, définir la nouvelle colonne et trier en ordre croissant
            setSortColumn(columnName);
            setSortOrder('asc');
        }
    };

    // Appliquer le tri aux ingrédients filtrés
    const sortedAndFilteredIngredients = (() => {
        const sorted = [...filteredIngredients];
        
        if (!sortColumn) return sorted;

        sorted.sort((a, b) => {
            let aVal, bVal;

            switch (sortColumn) {
                case 'name':
                    aVal = String(a.name || '').toLowerCase();
                    bVal = String(b.name || '').toLowerCase();
                    break;
                case 'type':
                    aVal = a.item_type === 'consumable' ? 1 : 0;
                    bVal = b.item_type === 'consumable' ? 1 : 0;
                    break;
                case 'stock':
                    aVal = computeCalculatedStock(a);
                    bVal = computeCalculatedStock(b);
                    break;
                case 'threshold':
                    aVal = Number(a.threshold || 0);
                    bVal = Number(b.threshold || 0);
                    break;
                case 'unit':
                    aVal = String(a.unit || '').toLowerCase();
                    bVal = String(b.unit || '').toLowerCase();
                    break;
                case 'value':
                    aVal = getStockValue(a);
                    bVal = getStockValue(b);
                    break;
                case 'status': {
                    const statusOrder = { 'En rupture': 0, 'Proche du seuil': 1, 'OK': 2 };
                    aVal = statusOrder[getStockStatus(a)] ?? 1;
                    bVal = statusOrder[getStockStatus(b)] ?? 1;
                    break;
                }
                default:
                    return 0;
            }

            // Comparaison
            if (typeof aVal === 'string') {
                return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });

        return sorted;
    })();

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Stock</p>
                        <h2 className="text-3xl font-semibold text-green-900">Tableau de bord du stock</h2>
                    </div>
                    <p className="max-w-xl text-sm text-green-600">
                        Aperçu des portions disponibles, alertes de rupture et historique des mouvements.
                    </p>
                </div>
            </section>

            <section className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h3 className="text-xl font-semibold text-green-900">Inventaire des stocks</h3>
                        <p className="text-sm text-green-600">Filtrez par type de produit pour voir les ingrédients et les consommables séparément.</p>
                    </div>
                    <div className="rounded-3xl bg-green-100 px-4 py-3 text-right text-sm text-green-900 shadow-sm">
                        <div className="text-xs uppercase tracking-[0.35em] text-green-500">Valeur totale du stock</div>
                        <div className="mt-1 text-lg font-semibold">{formatLocaleNumber(totalStockValue, 2)} Ar</div>
                    </div>
                    <div className="flex w-full max-w-2xl flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="flex flex-wrap gap-2">
                            {[
                                { key: 'all', label: 'Tous' },
                                { key: 'ingredient', label: 'Ingrédients' },
                                { key: 'consumable', label: 'Consommables' },
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => setActiveItemType(tab.key)}
                                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${activeItemType === tab.key ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        <input
                            type="search"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            className="w-full rounded-3xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900 shadow-sm focus:border-green-400 focus:outline-none"
                            placeholder="Rechercher un ingrédient..."
                        />
                        <button
                            type="button"
                            onClick={() => setAnalyticsModalOpen(true)}
                            className="rounded-full border border-green-500 bg-white px-3 py-2 text-sm font-semibold text-green-800 transition hover:bg-green-100"
                        >
                            Vue synthétique
                        </button>
                        <button
                            type="button"
                            onClick={() => exportPricesCsv(sortedAndFilteredIngredients, movements)}
                            className="rounded-full border border-green-400 bg-white px-3 py-2 text-sm font-semibold text-green-700 transition hover:bg-green-100"
                        >
                            Exporter prix CSV
                        </button>
                        <button
                            type="button"
                            onClick={() => exportStockDetails(sortedAndFilteredIngredients, movements, computeCalculatedStock, getStockValue)}
                            className="rounded-full border border-green-600 bg-green-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-green-700"
                        >
                            Exporter stock détaillé
                        </button>
                    </div>
                </div>

                {/* Filtres avancés */}
                <div className="mt-4">
                    <button
                        type="button"
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        className="mb-3 rounded-full border border-green-400 bg-green-50 px-4 py-2 text-sm font-semibold text-green-700 transition hover:bg-green-100"
                    >
                        {showAdvancedFilters ? '▼ Masquer filtres avancés' : '► Afficher filtres avancés'}
                    </button>

                    {showAdvancedFilters && (
                        <div className="mb-4 rounded-3xl border border-green-200 bg-green-50 p-4">
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                                {/* Filtre Nom */}
                                <div>
                                    <label className="mb-1 block text-xs font-semibold text-green-700">Nom</label>
                                    <input
                                        type="text"
                                        value={filterName}
                                        onChange={(e) => setFilterName(e.target.value)}
                                        placeholder="Filtrer par nom..."
                                        className="w-full rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                    />
                                </div>

                                {/* Filtre Type */}
                                <div>
                                    <label className="mb-1 block text-xs font-semibold text-green-700">Type</label>
                                    <select
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="w-full rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                    >
                                        <option value="all">Tous</option>
                                        <option value="ingredient">Ingrédient</option>
                                        <option value="consumable">Consommable</option>
                                    </select>
                                </div>

                                {/* Filtre Stock Min-Max */}
                                <div className="sm:col-span-2">
                                    <label className="mb-1 block text-xs font-semibold text-green-700">Stock (min - max)</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            value={filterStockMin}
                                            onChange={(e) => setFilterStockMin(e.target.value)}
                                            placeholder="Min"
                                            className="flex-1 rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                        />
                                        <input
                                            type="number"
                                            value={filterStockMax}
                                            onChange={(e) => setFilterStockMax(e.target.value)}
                                            placeholder="Max"
                                            className="flex-1 rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                {/* Filtre Seuil Min-Max */}
                                <div className="sm:col-span-2">
                                    <label className="mb-1 block text-xs font-semibold text-green-700">Seuil (min - max)</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            value={filterThresholdMin}
                                            onChange={(e) => setFilterThresholdMin(e.target.value)}
                                            placeholder="Min"
                                            className="flex-1 rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                        />
                                        <input
                                            type="number"
                                            value={filterThresholdMax}
                                            onChange={(e) => setFilterThresholdMax(e.target.value)}
                                            placeholder="Max"
                                            className="flex-1 rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                {/* Filtre Unité */}
                                <div>
                                    <label className="mb-1 block text-xs font-semibold text-green-700">Unité</label>
                                    <input
                                        type="text"
                                        value={filterUnit}
                                        onChange={(e) => setFilterUnit(e.target.value)}
                                        placeholder="Filtrer par unité..."
                                        className="w-full rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                    />
                                </div>

                                {/* Filtre Valeur du Stock Min-Max */}
                                <div className="sm:col-span-2">
                                    <label className="mb-1 block text-xs font-semibold text-green-700">Valeur du stock (min - max)</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            value={filterValueMin}
                                            onChange={(e) => setFilterValueMin(e.target.value)}
                                            placeholder="Min"
                                            className="flex-1 rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                        />
                                        <input
                                            type="number"
                                            value={filterValueMax}
                                            onChange={(e) => setFilterValueMax(e.target.value)}
                                            placeholder="Max"
                                            className="flex-1 rounded-2xl border border-green-200 bg-white px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                {/* Bouton Réinitialiser */}
                                <div className="flex items-end">
                                    <button
                                        type="button"
                                        onClick={resetFilters}
                                        className="w-full rounded-2xl border border-orange-300 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-100"
                                    >
                                        Réinitialiser
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-4 overflow-x-auto rounded-3xl border border-green-200 bg-green-50">
                    <table className="min-w-full divide-y divide-green-200 text-left text-sm text-slate-700">
                        <thead className="bg-green-100 text-green-900">
                            <tr>
                                <th className="px-4 py-3 font-medium">
                                    <button
                                        type="button"
                                        onClick={() => handleSort('name')}
                                        className="flex items-center gap-2 hover:text-green-700 transition"
                                    >
                                        Nom
                                        {sortColumn === 'name' && (
                                            <span className="text-lg">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                        )}
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium">
                                    <button
                                        type="button"
                                        onClick={() => handleSort('type')}
                                        className="flex items-center gap-2 hover:text-green-700 transition"
                                    >
                                        Type
                                        {sortColumn === 'type' && (
                                            <span className="text-lg">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                        )}
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium">
                                    <button
                                        type="button"
                                        onClick={() => handleSort('stock')}
                                        className="flex items-center gap-2 hover:text-green-700 transition"
                                    >
                                        Stock
                                        {sortColumn === 'stock' && (
                                            <span className="text-lg">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                        )}
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium">
                                    <button
                                        type="button"
                                        onClick={() => handleSort('threshold')}
                                        className="flex items-center gap-2 hover:text-green-700 transition"
                                    >
                                        Seuil
                                        {sortColumn === 'threshold' && (
                                            <span className="text-lg">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                        )}
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium">
                                    <button
                                        type="button"
                                        onClick={() => handleSort('unit')}
                                        className="flex items-center gap-2 hover:text-green-700 transition"
                                    >
                                        Unité
                                        {sortColumn === 'unit' && (
                                            <span className="text-lg">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                        )}
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium">
                                    <button
                                        type="button"
                                        onClick={() => handleSort('value')}
                                        className="flex items-center gap-2 hover:text-green-700 transition"
                                    >
                                        Valeur du stock
                                        {sortColumn === 'value' && (
                                            <span className="text-lg">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                        )}
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium">
                                    <button
                                        type="button"
                                        onClick={() => handleSort('status')}
                                        className="flex items-center gap-2 hover:text-green-700 transition"
                                    >
                                        État de stock
                                        {sortColumn === 'status' && (
                                            <span className="text-lg">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                        )}
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium">Détails</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-green-100 bg-white">
                            {(sortedAndFilteredIngredients || []).length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-4 py-6 text-center text-sm text-green-600">
                                        Aucun élément disponible pour ce filtre.
                                    </td>
                                </tr>
                            ) : (
                                sortedAndFilteredIngredients.map((ingredient) => {
                                    const calculatedStock = computeCalculatedStock(ingredient);
                                    const status = getStockStatus(ingredient);
                                    const isStockCritical = status === 'En rupture';
                                    const isNearThreshold = status === 'Proche du seuil';
                                    
                                    return (
                                    <tr key={ingredient.id} className={isStockCritical ? 'bg-rose-50' : isNearThreshold ? 'bg-amber-50' : ''}>
                                        <td className={`px-4 py-3 font-medium ${isStockCritical || isNearThreshold ? 'text-slate-900' : 'text-slate-900'}`}>
                                            <div className="flex flex-col">
                                                <span>{ingredient.name}{(ingredient.marque || ingredient.caracteristique) ? ` (${[ingredient.marque, ingredient.caracteristique].filter(Boolean).join(' ')})` : ''}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ingredient.item_type === 'consumable' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                {ingredient.item_type === 'consumable' ? 'Consommable' : 'Ingrédient'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">{formatLocaleNumber(calculatedStock, 0)} {ingredient.unit || '—'}</td>
                                        <td className="px-4 py-3">{formatLocaleNumber(ingredient.threshold, 0)} {ingredient.unit || '—'}</td>
                                        <td className="px-4 py-3">{ingredient.unit || '—'}</td>
                                        <td className="px-4 py-3">
                                            {(() => {
                                                const stockValue = getStockValue(ingredient);
                                                return stockValue > 0 ? `${formatLocaleNumber(stockValue, 2)} Ar` : '—';
                                            })()}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                                status === 'En rupture'
                                                    ? 'bg-rose-100 text-rose-700'
                                                    : status === 'Proche du seuil'
                                                    ? 'bg-amber-100 text-amber-700'
                                                    : 'bg-emerald-100 text-emerald-700'
                                            }`}>
                                                {status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => openSupplierModal(ingredient)}
                                                    className="rounded-full border border-green-400 bg-white px-3 py-1 text-xs font-semibold text-green-700 transition hover:bg-green-100"
                                                >
                                                    Voir fournisseur
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openDetailsModal(ingredient)}
                                                    className="rounded-full border border-green-400 bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 transition hover:bg-green-100"
                                                >
                                                    Détails
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <IngredientDetailsModal
                ingredient={selectedIngredient}
                isOpen={detailsModalOpen}
                onClose={() => setDetailsModalOpen(false)}
            />

            <SupplierPricesModal
                item={supplierModalItem}
                isOpen={supplierModalOpen}
                onClose={() => setSupplierModalOpen(false)}
                entries={supplierModalEntries}
            />

            {analyticsModalOpen && (
                <AnalyticsModal
                    items={sortedAndFilteredIngredients}
                    movements={movements}
                    computeCalculatedStock={computeCalculatedStock}
                    onClose={() => setAnalyticsModalOpen(false)}
                />
            )}

            {(alerts || []).length > 0 && (
                <div className="rounded-3xl border border-orange-500 bg-orange-500/10 p-5 text-orange-100 shadow-xl shadow-slate-950/20">
                    <h3 className="text-lg font-semibold">Alertes de stock</h3>
                    <div className="mt-3 space-y-2 text-sm text-orange-100/90">
                        {(alerts || []).map((item) => (
                            <div key={item.id} className="flex items-center justify-between rounded-3xl bg-orange-500/10 p-3">
                                <span>{item.name}</span>
                                <span className="rounded-full bg-orange-500/20 px-3 py-1 text-xs uppercase tracking-[0.2em]">
                                    Stock critique
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
                <div className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                    <h3 className="text-xl font-semibold text-green-900">Portions par plat</h3>
                    <div className="mt-4 space-y-4">
                        {(dishStatus || []).map((dish) => (
                            <div key={dish.id} className="rounded-3xl border border-green-400 bg-green-50 p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="font-semibold text-green-900">{dish.name}</p>
                                        <p className="text-sm text-green-600">Ar {dish.price.toFixed(2)}</p>
                                    </div>
                                    <span className="rounded-full bg-green-200 px-3 py-1 text-xs text-green-700">
                                        {dish.portions === null ? 'Fournisseur' : `${dish.portions} portions`}
                                    </span>
                                </div>
                                <div className="mt-3 h-2 overflow-hidden rounded-full bg-green-200">
                                    <div
                                        className="h-full rounded-full bg-green-600"
                                        style={{ width: dish.portions === null ? '100%' : `${Math.min(100, dish.portions * 10)}%` }}
                                    />
                                </div>
                                {dish.portions === null && (
                                    <p className="mt-2 text-xs text-green-500">Plat géré par un fournisseur — portions non calculées localement.</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                    <h3 className="text-xl font-semibold text-green-900">Ingrédients manquants</h3>
                    <div className="mt-4 space-y-3">
                        {(missingIngredients || []).length ? (
                            (missingIngredients || []).map((ingredient) => (
                                <div key={ingredient.id} className="rounded-3xl border border-green-400 bg-green-50 p-4">
                                    <p className="font-semibold text-green-900">{ingredient.name}</p>
                                    <p className="text-sm text-green-600">Il manque {ingredient.missing} {ingredient.unit} pour repasser au-dessus du seuil.</p>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-green-600">Pas d'ingrédients manquants pour l'instant.</p>
                        )}
                    </div>
                </div>
            </div>

            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <h3 className="text-xl font-semibold text-green-900">Historique des mouvements</h3>
                <div className="mt-4 overflow-hidden rounded-3xl border border-green-400 bg-white">
                    <table className="w-full border-collapse text-sm text-green-700">
                        <thead className="bg-green-50/80 text-green-600">
                            <tr>
                                <th className="px-4 py-3 text-left">Date</th>
                                <th className="px-4 py-3 text-left">Type</th>
                                <th className="px-4 py-3 text-left">Élément</th>
                                <th className="px-4 py-3 text-left">Quantité</th>
                                <th className="px-4 py-3 text-left">Raison</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(movements || []).map((movement) => (
                                <tr key={movement.id} className="border-t border-green-400">
                                    <td className="px-4 py-3">{new Date(movement.movement_date || movement.created_at || movement.date || '').toLocaleDateString('fr-FR')}</td>
                                    <td className="px-4 py-3">{getMovementOriginLabel(movement)}</td>
                                    <td className="px-4 py-3">{movement?.stockable?.name || movement.item || '—'}</td>
                                    <td className="px-4 py-3">{movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}</td>
                                    <td className="px-4 py-3 text-green-600">{movement.notes || movement.reference || movement.note || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
